/**
 * Discipline Store
 *
 * Manages trading discipline state including:
 * - Lock modes (cool-off, done for day, revenge guard, tilt protection)
 * - Size discipline (lock after loss, forced reduction, gradual recovery)
 * - Stop protection (tighten-only mode, widen limits)
 * - 2FA unlock flow
 *
 * Connected to tradovate-service backend via Socket.IO for persistence and sync.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { WebSocketService } from '../services/WebSocketService';
import { addToast } from './toastStore';
import { useRestrainStore, clampRestrainMinutes, RESTRAIN_DEFAULT_MINUTES, handleRestrainLockedError } from './restrainStore';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type LockMode = 'unlocked' | 'cool_off' | 'done_for_day' | 'revenge_guard' | 'tilt_protection';
export type StopMode = 'flexible' | 'tighten_only' | 'locked';
export type SizeRecoveryMode = 'instant' | 'gradual';

export interface DisciplineSettings {
  // Time locks
  coolOffDefaultMinutes: number;

  // Auto-locks
  revengeGuardEnabled: boolean;
  revengeGuardMinutes: number;
  tiltProtectionEnabled: boolean;
  tiltProtectionLosses: number;
  tiltProtectionMinutes: number;
  escalatingCooldownEnabled: boolean;
  escalatingCooldownMinutes: number[];

  // Size discipline
  sizeLockAfterLoss: boolean;
  forcedReductionEnabled: boolean;
  forcedReductionAfterLosses: number;
  minimumSize: number;
  sizeRecoveryMode: SizeRecoveryMode;

  // Stop protection
  stopMode: StopMode;
  stopModificationDelaySeconds: number;
  maxWidensPerTrade: number;
  maxWidenTicks: number;
  dailyWidenLimit: number;

  // 2FA
  twoFactorEnabled: boolean;
  twoFactorEmail: string;
  reflectionRequired: boolean;
  reflectionMinChars: number;
  otpDelaySeconds: {
    cool_off: number;
    revenge_guard: number;
    tilt_protection: number;
    done_for_day: number;
  };

  // Emergency
  emergencyUnlockEnabled: boolean;
  emergencyCooldownHours: number;

  // Simple unlock (no OTP)
  simpleUnlockEnabled: boolean;

  // Restrain: freeze position size while in a position (see restrainStore).
  // Two independent venues: futures (Tradovate-family) and perps (Vest).
  restrainFuturesEnabled: boolean;
  /** Minutes from position open, 0..480; 0 = until flat. */
  restrainFuturesMinutes: number;
  restrainPerpsEnabled: boolean;
  restrainPerpsMinutes: number;
  /** LEGACY single toggle: always a mirror of futures (an older backend sends only this). */
  restrainEnabled: boolean;
  /** LEGACY: mirror of restrainFuturesMinutes. */
  restrainMinutes: number;
}

export interface PendingUnlock {
  otpSentAt: number;
  otpValidAt: number;
  otpExpiresAt: number;
  reflectionText: string | null;
  attempts: number;
}

export interface UnlockAttempt {
  timestamp: number;
  lockMode: LockMode;
  lockReason: string;
  unlockMethod: 'timeout' | 'otp' | 'emergency';
  reflectionText: string | null;
  success: boolean;
}

export interface DisciplineState {
  // Lock status
  lockMode: LockMode;
  lockReason: string | null;
  lockedAt: number | null;
  lockExpiresAt: number | null;

  // Pending unlock (2FA in progress)
  pendingUnlock: PendingUnlock | null;

  // Session tracking
  consecutiveLosses: number;
  consecutiveWins: number;
  lastTradeResult: 'win' | 'loss' | null;
  lastTradeTime: number | null;

  // Size state
  baseMaxSize: number;
  currentMaxSize: number;
  sizeReductionLevel: number;
  sizeLocked: boolean;

  // Stop tracking
  widensToday: number;
  currentTradeWidens: number;

  // Emergency
  lastEmergencyUnlock: number | null;
  emergencyUnlocksToday: number;

  // Unlock history
  unlockHistory: UnlockAttempt[];
}

// Backend status response format
export interface DisciplineStatus {
  lockMode: LockMode;
  lockReason: string | null;
  lockedAt: number | null;
  lockExpiresAt: number | null;
  hasPendingUnlock: boolean;
  otpValidAt: number | null;
  otpExpiresAt: number | null;
  baseMaxSize: number;
  currentMaxSize: number;
  sizeReductionLevel: number;
  sizeLocked: boolean;
  consecutiveLosses: number;
  consecutiveWins: number;
  lastTradeResult: string | null;
  stopMode: StopMode;
  widensToday: number;
  currentTradeWidens: number;
  emergencyAvailable: boolean;
  emergencyUnlocksToday: number;
  /** Restrain state (same shape as the rms:restrain broadcast); absent on older backends. */
  restrain?: unknown;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_DISCIPLINE_SETTINGS: DisciplineSettings = {
  // Time locks
  coolOffDefaultMinutes: 15,

  // Auto-locks
  revengeGuardEnabled: false,
  revengeGuardMinutes: 5,
  tiltProtectionEnabled: false,
  tiltProtectionLosses: 3,
  tiltProtectionMinutes: 30,
  escalatingCooldownEnabled: false,
  escalatingCooldownMinutes: [5, 15, 30, 60],

  // Size discipline
  sizeLockAfterLoss: true,
  forcedReductionEnabled: true,
  forcedReductionAfterLosses: 2,
  minimumSize: 1,
  sizeRecoveryMode: 'gradual',

  // Stop protection
  stopMode: 'tighten_only',
  stopModificationDelaySeconds: 30,
  maxWidensPerTrade: 1,
  maxWidenTicks: 20,
  dailyWidenLimit: 5,

  // 2FA
  twoFactorEnabled: true,
  twoFactorEmail: '',
  reflectionRequired: true,
  reflectionMinChars: 50,
  otpDelaySeconds: {
    cool_off: 60,
    revenge_guard: 120,
    tilt_protection: 300,
    done_for_day: 600,
  },

  // Emergency
  emergencyUnlockEnabled: true,
  emergencyCooldownHours: 24,

  // Simple unlock
  simpleUnlockEnabled: true,

  // Restrain (off until the user turns it on), per venue
  restrainFuturesEnabled: false,
  restrainFuturesMinutes: RESTRAIN_DEFAULT_MINUTES,
  restrainPerpsEnabled: false,
  restrainPerpsMinutes: RESTRAIN_DEFAULT_MINUTES,
  restrainEnabled: false,
  restrainMinutes: RESTRAIN_DEFAULT_MINUTES,
};

/**
 * Server settings over the defaults: an older backend doesn't know the newer
 * fields (restrain*), and the UI must still render them with sane values.
 */
export function normalizeDisciplineSettings(raw: Partial<DisciplineSettings> | null | undefined): DisciplineSettings {
  const r = raw ?? {};
  const merged = { ...DEFAULT_DISCIPLINE_SETTINGS, ...r };
  // Never saved (missing / null) -> the default; an explicit 0 means until flat and is kept.
  const minutesOf = (v: unknown): number => (v == null ? RESTRAIN_DEFAULT_MINUTES : clampRestrainMinutes(v));
  // A venue key the server did not send (older backend: one toggle for both) falls back to the legacy pair.
  const futuresEnabled = (r.restrainFuturesEnabled ?? r.restrainEnabled) === true;
  const futuresMinutes = minutesOf(r.restrainFuturesMinutes ?? r.restrainMinutes);
  return {
    ...merged,
    restrainFuturesEnabled: futuresEnabled,
    restrainFuturesMinutes: futuresMinutes,
    restrainPerpsEnabled: (r.restrainPerpsEnabled ?? r.restrainEnabled) === true,
    restrainPerpsMinutes: minutesOf(r.restrainPerpsMinutes ?? r.restrainMinutes),
    restrainEnabled: futuresEnabled,
    restrainMinutes: futuresMinutes,
  };
}

const DEFAULT_DISCIPLINE_STATE: DisciplineState = {
  lockMode: 'unlocked',
  lockReason: null,
  lockedAt: null,
  lockExpiresAt: null,
  pendingUnlock: null,
  consecutiveLosses: 0,
  consecutiveWins: 0,
  lastTradeResult: null,
  lastTradeTime: null,
  baseMaxSize: 4,
  currentMaxSize: 4,
  sizeReductionLevel: 0,
  sizeLocked: false,
  widensToday: 0,
  currentTradeWidens: 0,
  lastEmergencyUnlock: null,
  emergencyUnlocksToday: 0,
  unlockHistory: [],
};

// ============================================================================
// WEBSOCKET SERVICE REFERENCE
// ============================================================================

let wsService: WebSocketService | null = null;

/**
 * Set the WebSocket service reference (called during initialization)
 */
export function setDisciplineWsService(service: WebSocketService): void {
  wsService = service;
}

/**
 * Emit a socket event (with null check)
 */
function emit(event: string, data?: any): void {
  if (wsService) {
    console.log(`[DISCIPLINE] Emitting ${event}`, data);
    wsService.emit(event, data);
  } else {
    console.warn(`[DISCIPLINE] Cannot emit ${event} - wsService not initialized`);
  }
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface DisciplineStore {
  // Settings
  settings: DisciplineSettings;

  // State
  state: DisciplineState;

  // UI state
  unlockModalOpen: boolean;
  settingsModalOpen: boolean;

  // Connection state
  isConnected: boolean;

  // Computed
  isLocked: () => boolean;
  getTimeRemaining: () => number | null;
  canEmergencyUnlock: () => boolean;

  // Lock actions (emit to backend)
  setLockMode: (mode: LockMode, reason?: string, durationMinutes?: number) => void;
  startCoolOff: (minutes?: number) => void;
  startCoolOffUntil: (untilTimestamp: number) => void;
  setDoneForDay: () => void;

  // Unlock actions (emit to backend)
  requestUnlock: (reflectionText: string) => Promise<{ success: boolean; error?: string; validInSeconds?: number }>;
  verifyOTP: (otp: string) => Promise<{ success: boolean; error?: string }>;
  emergencyUnlock: () => Promise<{ success: boolean; error?: string; warning?: string }>;
  simpleUnlock: () => Promise<{ success: boolean; error?: string }>;
  cancelUnlock: () => void;

  // Size discipline
  setBaseMaxSize: (size: number) => void;
  recordTradeResult: (result: 'win' | 'loss') => void;

  // Settings
  updateSettings: (partial: Partial<DisciplineSettings>) => void;

  // Fetch from backend
  fetchStatus: () => void;
  fetchSettings: () => void;

  // UI
  openUnlockModal: () => void;
  closeUnlockModal: () => void;
  openSettingsModal: () => void;
  closeSettingsModal: () => void;

  // Internal - for socket handlers
  _setStatus: (status: DisciplineStatus) => void;
  _setSettings: (settings: DisciplineSettings) => void;
  _setConnected: (connected: boolean) => void;
  _setPendingUnlock: (pending: PendingUnlock | null) => void;
  _checkAutoUnlock: () => void;
  _resetToDefaults: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useDisciplineStore = create<DisciplineStore>((set, get) => {
  // Auto-unlock timer
  let autoUnlockTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleAutoUnlock = (expiresAt: number) => {
    if (autoUnlockTimer) {
      clearTimeout(autoUnlockTimer);
    }
    const remaining = expiresAt - Date.now();
    if (remaining > 0) {
      autoUnlockTimer = setTimeout(() => {
        get()._checkAutoUnlock();
      }, remaining);
    }
  };

  return {
    // Initial state
    settings: DEFAULT_DISCIPLINE_SETTINGS,
    state: DEFAULT_DISCIPLINE_STATE,
    unlockModalOpen: false,
    settingsModalOpen: false,
    isConnected: false,

    // Computed
    isLocked: () => get().state.lockMode !== 'unlocked',

    getTimeRemaining: () => {
      const { lockExpiresAt } = get().state;
      if (!lockExpiresAt) return null;
      const remaining = lockExpiresAt - Date.now();
      return remaining > 0 ? remaining : 0;
    },

    canEmergencyUnlock: () => {
      const { settings, state } = get();
      if (!settings.emergencyUnlockEnabled) return false;
      if (!state.lastEmergencyUnlock) return true;
      const hoursSince = (Date.now() - state.lastEmergencyUnlock) / (1000 * 60 * 60);
      return hoursSince >= settings.emergencyCooldownHours;
    },

    // Lock actions - emit to backend
    setLockMode: (mode, reason, durationMinutes) => {
      emit('rms:discipline-set-lock', { mode, reason, minutes: durationMinutes });
    },

    startCoolOff: (minutes) => {
      emit('rms:discipline-start-cooloff', { minutes });
    },

    startCoolOffUntil: (untilTimestamp) => {
      emit('rms:discipline-start-cooloff-until', { untilTimestamp });
    },

    setDoneForDay: () => {
      emit('rms:discipline-done-for-day');
    },

    // Unlock actions - emit to backend and return promise
    requestUnlock: async (reflectionText) => {
      return new Promise((resolve) => {
        if (!wsService) {
          resolve({ success: false, error: 'Not connected to server' });
          return;
        }

        // Set up one-time listener for response
        const handleResponse = (data: { success: boolean; error?: string; validInSeconds?: number }) => {
          wsService?.off('rms:discipline-unlock-requested', handleResponse);
          resolve(data);
        };

        wsService.on('rms:discipline-unlock-requested', handleResponse);
        emit('rms:discipline-request-unlock', { reflectionText });

        // Timeout after 10 seconds
        setTimeout(() => {
          wsService?.off('rms:discipline-unlock-requested', handleResponse);
          resolve({ success: false, error: 'Request timed out' });
        }, 10000);
      });
    },

    verifyOTP: async (otp) => {
      return new Promise((resolve) => {
        if (!wsService) {
          resolve({ success: false, error: 'Not connected to server' });
          return;
        }

        const handleResponse = (data: { success: boolean; error?: string }) => {
          wsService?.off('rms:discipline-otp-verified', handleResponse);
          if (data.success) {
            set({ unlockModalOpen: false });
          }
          resolve(data);
        };

        wsService.on('rms:discipline-otp-verified', handleResponse);
        emit('rms:discipline-verify-otp', { otp });

        setTimeout(() => {
          wsService?.off('rms:discipline-otp-verified', handleResponse);
          resolve({ success: false, error: 'Request timed out' });
        }, 10000);
      });
    },

    emergencyUnlock: async () => {
      return new Promise((resolve) => {
        if (!wsService) {
          resolve({ success: false, error: 'Not connected to server' });
          return;
        }

        const handleResponse = (data: { success: boolean; error?: string; warning?: string }) => {
          wsService?.off('rms:discipline-emergency-result', handleResponse);
          if (data.success) {
            set({ unlockModalOpen: false });
          }
          resolve(data);
        };

        wsService.on('rms:discipline-emergency-result', handleResponse);
        emit('rms:discipline-emergency-unlock');

        setTimeout(() => {
          wsService?.off('rms:discipline-emergency-result', handleResponse);
          resolve({ success: false, error: 'Request timed out' });
        }, 10000);
      });
    },

    simpleUnlock: async () => {
      return new Promise((resolve) => {
        if (!wsService) {
          resolve({ success: false, error: 'Not connected to server' });
          return;
        }

        const handleResponse = (data: { success: boolean; error?: string }) => {
          wsService?.off('rms:discipline-simple-unlock-result', handleResponse);
          if (data.success) {
            set({ unlockModalOpen: false });
          }
          resolve(data);
        };

        wsService.on('rms:discipline-simple-unlock-result', handleResponse);
        emit('rms:discipline-simple-unlock');

        setTimeout(() => {
          wsService?.off('rms:discipline-simple-unlock-result', handleResponse);
          resolve({ success: false, error: 'Request timed out' });
        }, 10000);
      });
    },

    cancelUnlock: () => {
      emit('rms:discipline-cancel-unlock');
    },

    // Size discipline
    setBaseMaxSize: (size) => {
      emit('rms:discipline-set-base-size', { size });
    },

    recordTradeResult: (result) => {
      emit('rms:discipline-record-trade', { result });
    },

    // Settings
    updateSettings: (partial) => {
      // Optimistic when we can actually send it; the server's
      // rms:discipline-settings broadcast is the reconciliation.
      if (wsService?.isConnected()) {
        set((s) => ({ settings: normalizeDisciplineSettings({ ...s.settings, ...partial }) }));
      }
      emit('rms:discipline-update-settings', partial);
    },

    // Fetch from backend
    fetchStatus: () => {
      emit('rms:discipline-get-status');
    },

    fetchSettings: () => {
      emit('rms:discipline-get-settings');
    },

    // UI
    openUnlockModal: () => set({ unlockModalOpen: true }),
    closeUnlockModal: () => set({ unlockModalOpen: false }),
    openSettingsModal: () => set({ settingsModalOpen: true }),
    closeSettingsModal: () => set({ settingsModalOpen: false }),

    // Internal - called by socket handlers
    _setStatus: (status) => {
      const prevLockMode = get().state.lockMode;

      set((s) => ({
        state: {
          ...s.state,
          lockMode: status.lockMode,
          lockReason: status.lockReason,
          lockedAt: status.lockedAt,
          lockExpiresAt: status.lockExpiresAt,
          baseMaxSize: status.baseMaxSize,
          currentMaxSize: status.currentMaxSize,
          sizeReductionLevel: status.sizeReductionLevel,
          sizeLocked: status.sizeLocked,
          consecutiveLosses: status.consecutiveLosses,
          consecutiveWins: status.consecutiveWins,
          lastTradeResult: status.lastTradeResult as 'win' | 'loss' | null,
          widensToday: status.widensToday,
          currentTradeWidens: status.currentTradeWidens,
          emergencyUnlocksToday: status.emergencyUnlocksToday,
          pendingUnlock: status.hasPendingUnlock && status.otpValidAt && status.otpExpiresAt ? {
            otpSentAt: Date.now(),
            otpValidAt: status.otpValidAt,
            otpExpiresAt: status.otpExpiresAt,
            reflectionText: null,
            attempts: 0,
          } : null,
        },
      }));

      // Show toast when lock mode changes
      if (prevLockMode !== status.lockMode) {
        if (status.lockMode === 'unlocked') {
          addToast('success', 'Trading unlocked', 3000);
        } else if (prevLockMode === 'unlocked') {
          // Just became locked
          const modeLabels: Record<string, string> = {
            cool_off: 'Cool-off',
            done_for_day: 'Done for day',
            revenge_guard: 'Revenge guard',
            tilt_protection: 'Tilt protection',
          };
          const label = modeLabels[status.lockMode] || status.lockMode;
          addToast('warning', `Trading locked: ${label}`, 4000);
        }
      }

      // Restrain state can ride along on the status (newer backends only).
      // The status is per tradovate-service instance (not merged by the
      // engine), so it only seeds the store until the merged rms:restrain
      // arrives - it must never clear another instance's restrains.
      if (status.restrain) {
        useRestrainStore.getState().applyRestrainStatus(status.restrain);
      }

      // Schedule auto-unlock if applicable
      if (status.lockExpiresAt) {
        scheduleAutoUnlock(status.lockExpiresAt);
      }
    },

    _setSettings: (settings) => {
      set({ settings: normalizeDisciplineSettings(settings) });
    },

    _setConnected: (connected) => {
      set({ isConnected: connected });
    },

    _setPendingUnlock: (pending) => {
      set((s) => ({
        state: { ...s.state, pendingUnlock: pending },
      }));
    },

    _checkAutoUnlock: () => {
      const { state } = get();
      if (state.lockMode !== 'unlocked' && state.lockExpiresAt && Date.now() >= state.lockExpiresAt) {
        // Request status from backend - it will have unlocked automatically
        get().fetchStatus();
      }
    },

    _resetToDefaults: () => {
      // Reset state to defaults on reconnect to clear any stale data
      // Backend will push fresh state immediately
      set({ state: DEFAULT_DISCIPLINE_STATE });
      console.log('[DISCIPLINE] State reset to defaults, awaiting fresh state from backend');
    },
  };
});

// ============================================================================
// SOCKET HANDLERS INITIALIZATION
// ============================================================================

let handlersInitialized = false;

/**
 * Initialize discipline socket handlers
 * Called by WebSocketService after connection
 */
export function initDisciplineSocketHandlers(service: WebSocketService): void {
  setDisciplineWsService(service);

  const store = useDisciplineStore.getState();
  store._setConnected(true);

  // Always reset to default state on connect to clear any stale data
  // Backend will push fresh state immediately after connection
  store._resetToDefaults();

  if (handlersInitialized) {
    console.log('[DISCIPLINE] Socket handlers already initialized, fetching fresh status');
    // Handlers exist but fetch fresh status to ensure sync
    store.fetchStatus();
    return;
  }

  handlersInitialized = true;

  // Listen for status updates
  service.on('rms:discipline-status', (status: DisciplineStatus) => {
    console.log('[DISCIPLINE] Status received:', status.lockMode);
    useDisciplineStore.getState()._setStatus(status);
  });

  // Listen for settings updates
  service.on('rms:discipline-settings', (settings: DisciplineSettings) => {
    console.log('[DISCIPLINE] Settings received');
    useDisciplineStore.getState()._setSettings(settings);
  });

  // Listen for unlock events
  service.on('rms:discipline-unlocked', () => {
    console.log('[DISCIPLINE] Unlocked');
    useDisciplineStore.getState().fetchStatus();
  });

  // Listen for auto-lock events
  service.on('rms:discipline-auto-locked', (data: { mode: LockMode; reason: string }) => {
    console.log('[DISCIPLINE] Auto-locked:', data.mode, data.reason);
    useDisciplineStore.getState().fetchStatus();
  });

  // Listen for size changes
  service.on('rms:discipline-size-reduced', (data: { newMaxSize: number; baseMaxSize: number; reason: string }) => {
    console.log('[DISCIPLINE] Size reduced:', data.newMaxSize, 'from', data.baseMaxSize);
  });

  service.on('rms:discipline-size-recovered', (data: { newMaxSize: number; baseMaxSize: number; fullyRecovered: boolean }) => {
    console.log('[DISCIPLINE] Size recovered:', data.newMaxSize, data.fullyRecovered ? '(full)' : '');
  });

  // Listen for emergency unlock usage
  service.on('rms:discipline-emergency-used', () => {
    console.warn('[DISCIPLINE] Emergency unlock was used!');
  });

  // Listen for errors
  service.on('rms:discipline-error', (data: { message: string; code?: string }) => {
    // A running restrain refused an off/duration change (the rest of the save went through).
    if (handleRestrainLockedError(data)) return;
    console.error('[DISCIPLINE] Error:', data.message);
  });

  console.log('[DISCIPLINE] Socket handlers initialized');
}

/**
 * Reset handlers flag (for reconnection)
 */
export function resetDisciplineSocketHandlers(): void {
  handlersInitialized = false;
  useDisciplineStore.getState()._setConnected(false);
}

// ============================================================================
// SELECTORS
// ============================================================================

export const disciplineSelectors = {
  useLockMode: () => useDisciplineStore((s) => s.state.lockMode),
  useLockReason: () => useDisciplineStore((s) => s.state.lockReason),
  useLockExpiresAt: () => useDisciplineStore((s) => s.state.lockExpiresAt),
  useIsLocked: () => useDisciplineStore((s) => s.state.lockMode !== 'unlocked'),
  // useShallow is load-bearing: this selector builds a NEW object every call,
  // and zustand compares snapshots with Object.is, so without it the store looks
  // changed on every render and the component loops until React throws #185.
  // Unused today, which is the only reason it hasn't bitten — the same shape in
  // FloatingOrderPanel took the chart down on 2026-08-03.
  useSizeState: () =>
    useDisciplineStore(
      useShallow((s) => ({
        baseMaxSize: s.state.baseMaxSize,
        currentMaxSize: s.state.currentMaxSize,
        sizeReductionLevel: s.state.sizeReductionLevel,
        sizeLocked: s.state.sizeLocked,
      }))
    ),
  useSettings: () => useDisciplineStore((s) => s.settings),
  useStopMode: () => useDisciplineStore((s) => s.settings.stopMode),
};
